import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "./supabaseClient"; // make sure this points to your initialized Supabase client
import { API_BASE_URL, COURSE_DETAILS_FILE } from './config';

export default function SemesterCourseIntake() {
  const navigate = useNavigate();
  const { index } = useParams(); // index of current semester in selectedSemesters
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userId, setUserId] = useState(null); // Store user ID

  // Load selected semesters from localStorage
  const selectedSemesters = JSON.parse(localStorage.getItem("selectedSemesters") || "[]");
  const semesterCourses = JSON.parse(localStorage.getItem("semesterCourses") || "{}");
  const semester = selectedSemesters[parseInt(index)];

  // Check if we came from transcript upload
  const fromTranscriptUpload = localStorage.getItem('fromTranscriptUpload') === 'true';
  const editingSemesterIndex = localStorage.getItem('editingSemesterIndex');

  const backendUrl=process.env.REACT_APP_BACKEND_URL;

  // Load courses JSON dynamically on mount or semester change
  useEffect(() => {
    if (!semester) {
      navigate("/graph");
      return;
    }

    async function fetchCourses() {
      try {
        const res = await fetch(`/${COURSE_DETAILS_FILE}`);
        const allData = await res.json();
        // Filter courses by the current semester
        const data = allData.filter(course => 
          course.semester && course.semester.includes(semester)
        );
        setAllCourses(data);
        setSelectedCourses(semesterCourses[semester] || []);
      } catch (error) {
        console.error("Failed to load courses JSON", error);
        setAllCourses([]);
      }
    }

    async function fetchUserId() {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();

        if (error) {
          console.error("Error fetching user:", error);
          return;
        }

        if (user) {
          setUserId(user.id);
        } else {
          console.warn("No user logged in.");
        }
      } catch (err) {
        console.error("Unexpected error fetching user:", err);
      }
    }

    fetchCourses();
    fetchUserId(); // Fetch user ID when component mounts
  }, [semester, navigate]);

  const filteredCourses = allCourses.filter((course) => {
    const codesArray = Array.isArray(course.course_codes)
      ? course.course_codes
      : [course.course_codes];
  
    const codesMatch = codesArray.some((code) =>
      code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  
    const titleMatch = course.course_title && 
      course.course_title.toLowerCase().includes(searchTerm.toLowerCase());
  
    return codesMatch || titleMatch;
  });

  // Separate selected and unselected courses
  const { selectedCoursesData, unselectedCourses } = useMemo(() => {
    const uniqueCoursesMap = new Map();

    // First, add all courses that match the search filter
    filteredCourses.forEach((course) => {
      const courseCodesArray = Array.isArray(course.course_codes)
        ? course.course_codes
        : [course.course_codes];
      const courseCode = courseCodesArray[0];

      // Only add first occurrence of this courseCode
      if (!uniqueCoursesMap.has(courseCode)) {
        uniqueCoursesMap.set(courseCode, course);
      }
    });

    // Then, ensure ALL selected courses are included, even if they don't match search
    allCourses.forEach((course) => {
      const courseCodesArray = Array.isArray(course.course_codes)
        ? course.course_codes
        : [course.course_codes];
      const courseCode = courseCodesArray[0];

      // Check if any of the course codes are in selectedCourses
      const isSelected = courseCodesArray.some(code => selectedCourses.includes(code));

      // If this course is selected but not already in the map, add it
      if (isSelected && !uniqueCoursesMap.has(courseCode)) {
        uniqueCoursesMap.set(courseCode, course);
      }
    });

    const coursesArray = Array.from(uniqueCoursesMap.values());
    
    // Separate into selected and unselected
    const selected = [];
    const unselected = [];
    
    coursesArray.forEach(course => {
      const courseCodesArray = Array.isArray(course.course_codes) ? course.course_codes : [course.course_codes];
      const courseCode = courseCodesArray[0];
      
      // Check if any of the course codes are in selectedCourses
      const isSelected = courseCodesArray.some(code => selectedCourses.includes(code));
      
      if (isSelected) {
        selected.push(course);
      } else {
        unselected.push(course);
      }
    });
    
    // Sort both arrays alphabetically
    const sortFn = (a, b) => {
      const aCode = Array.isArray(a.course_codes) ? a.course_codes[0] : a.course_codes;
      const bCode = Array.isArray(b.course_codes) ? b.course_codes[0] : b.course_codes;
      return aCode.localeCompare(bCode);
    };
    
    return {
      selectedCoursesData: selected.sort(sortFn),
      unselectedCourses: unselected.sort(sortFn)
    };
  }, [allCourses, selectedCourses, filteredCourses]);

  const toggleCourse = (course) => {
    const courseCodesArray = Array.isArray(course.course_codes) 
      ? course.course_codes 
      : [course.course_codes];
    
    setSelectedCourses((prev) => {
      // Check if any of the course codes are currently selected
      const isCurrentlySelected = courseCodesArray.some(code => prev.includes(code));
      
      if (isCurrentlySelected) {
        // Remove all course codes for this course
        return prev.filter(code => !courseCodesArray.includes(code));
      } else {
        // Add all course codes for this course
        return [...prev, ...courseCodesArray];
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && unselectedCourses.length > 0) {
      e.preventDefault(); // Prevent form submission if this input is in a form
      const topCourse = unselectedCourses[0];
      toggleCourse(topCourse);
    }
  };

  const handleReturnToTranscriptUpload = () => {
    // Update the semester courses with current selection
    const updatedSemesterCourses = { ...semesterCourses };
    updatedSemesterCourses[semester] = selectedCourses;
    localStorage.setItem("semesterCourses", JSON.stringify(updatedSemesterCourses));

    // Update the transcript data structure
    const editingTranscriptData = JSON.parse(localStorage.getItem('editingTranscriptData') || '{}');
    if (editingTranscriptData.semesters && editingTranscriptData.semesters[parseInt(editingSemesterIndex)]) {
      editingTranscriptData.semesters[parseInt(editingSemesterIndex)].courses = selectedCourses;
      localStorage.setItem('editingTranscriptData', JSON.stringify(editingTranscriptData));
    }

    // Set flag to indicate return from edit
    localStorage.setItem('returnFromTranscriptEdit', 'true');
    
    // Navigate back to transcript upload
    navigate('/upload');
  };

  const handleSubmit = async () => {

    const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        console.error("No valid session token found");
        return;
      }

    // If we came from transcript upload, handle return differently
    if (fromTranscriptUpload) {
      handleReturnToTranscriptUpload();
      return;
    }

    // Original submit logic for normal flow
    semesterCourses[semester] = selectedCourses;
    localStorage.setItem("semesterCourses", JSON.stringify(semesterCourses));

    const isLastSemester = parseInt(index) + 1 >= selectedSemesters.length;

    if (isLastSemester) {
      // Build the dictionary payload
      const payload = {
        //user_id: userId,
        semester_courses: {}
      };

      for (const [sem, courses] of Object.entries(semesterCourses)) {
        payload.semester_courses[sem] = courses;
      }

      try {
        const response = await fetch(`${backendUrl}/submit_courses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}` // Add JWT token
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        console.log("Successfully sent data to backend!");
      } catch (error) {
        console.error("Error sending data to backend:", error);
      }

      navigate("/graph");
    } else {
      navigate(`/intake/courses/${parseInt(index) + 1}`);
    }
  };

  const handleBack = () => {
    if (fromTranscriptUpload) {
      // If we came from transcript upload, go back to transcript upload
      handleReturnToTranscriptUpload();
    } else {
      // Original back logic
      if (parseInt(index) === 0) {
        navigate('/intake');
      } else {
        navigate(`/intake/courses/${parseInt(index) - 1}`);
      }
    }
  };

  if (!semester) return null;

  return (
    <div className="h-screen w-full overflow-y-auto bg-canvas">
    <div className="card mx-auto my-12 max-w-3xl space-y-5 p-7">
      <div className="text-center">
        <span className="badge-primary mb-2">{semester}</span>
        <h2 className="font-display text-2xl font-semibold text-ink">
          {fromTranscriptUpload ? `Edit courses for ${semester}` : `Select courses you took`}
        </h2>
      </div>

      {fromTranscriptUpload && (
        <div className="rounded-lg border border-primary/20 bg-primary-50 p-4">
          <p className="text-sm text-primary">
            You're editing courses from your transcript. Click "Save changes" when done to return to the validation page.
          </p>
        </div>
      )}

      <input
        type="text"
        placeholder="Search courses…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        className="input"
      />

      {/* Fixed Selected Courses Section */}
      {selectedCoursesData.length > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary-50/60 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary">
            Selected courses ({selectedCoursesData.length})
          </div>
          <div className="space-y-1.5">
            {selectedCoursesData.map((course) => {
              const courseCodesArray = Array.isArray(course.course_codes)
                ? course.course_codes
                : [course.course_codes];
              const courseCode = courseCodesArray[0];
              const courseCodesDisplay = courseCodesArray.join(", ");

              return (
                <label
                  key={`selected-${courseCode}`}
                  htmlFor={`selected-${courseCode}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-line bg-surface p-2.5"
                >
                  <input
                    type="checkbox"
                    id={`selected-${courseCode}`}
                    checked={true}
                    onChange={(e) => {
                      e.preventDefault();
                      toggleCourse(course);
                    }}
                    className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
                  />
                  <span className="flex-1 text-sm text-primary">
                    <span className="font-semibold">{courseCodesDisplay}</span>
                    {course.course_title && <span>: {course.course_title}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Searchable/Scrollable Unselected Courses */}
      <div className="max-h-96 space-y-1 overflow-y-auto rounded-xl border border-line p-3">
        {unselectedCourses.length === 0 && selectedCoursesData.length === 0 && (
          <p className="py-6 text-center text-sm text-ink-soft">No courses found.</p>
        )}

        {unselectedCourses.length === 0 && selectedCoursesData.length > 0 && searchTerm && (
          <p className="py-6 text-center text-sm text-ink-soft">No additional courses found matching "{searchTerm}".</p>
        )}

        {unselectedCourses.map((course) => {
          const courseCodesArray = Array.isArray(course.course_codes)
            ? course.course_codes
            : [course.course_codes];
          const courseCode = courseCodesArray[0];
          const courseCodesDisplay = courseCodesArray.join(", ");

          return (
            <label
              key={`unselected-${courseCode}`}
              htmlFor={`unselected-${courseCode}`}
              className="flex cursor-pointer items-center gap-3 rounded-lg p-2.5 transition-colors hover:bg-surface-alt"
            >
              <input
                type="checkbox"
                id={`unselected-${courseCode}`}
                checked={false}
                onChange={(e) => {
                  e.preventDefault();
                  toggleCourse(course);
                }}
                className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
              />
              <span className="flex-1 text-sm text-ink">
                <span className="font-medium">{courseCodesDisplay}</span>
                {course.course_title && <span className="text-ink-muted">: {course.course_title}</span>}
              </span>
            </label>
          );
        })}
      </div>

      <div className="flex gap-3">
        <button onClick={handleBack} className="btn-secondary flex-1">
          {fromTranscriptUpload ? "Cancel" : "Back"}
        </button>
        <button onClick={handleSubmit} className="btn-primary flex-1">
          {fromTranscriptUpload
            ? "Save changes"
            : (parseInt(index) + 1 < selectedSemesters.length ? "Next semester" : "Finish")
          }
        </button>
      </div>
    </div>
    </div>
  );
}